import {
  MESSAGE_TYPE_LABELS,
  TRANSCRIPT_STATUS_LABELS,
  type Direction,
  type MediaStatus,
  type MessageType,
  type TranscriptStatus,
} from '@convometry/contracts';
import { AlertTriangle, Ban, FileText, Hourglass, Image as ImageIcon, Mic, Paperclip, Pencil, Video } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDuration, formatTime } from '../../lib/format.js';

export interface MessageBubbleProps {
  id?: string;
  direction: Direction;
  sentAt: Date | string;
  timeZone: string;
  type?: MessageType;
  body?: string | null;
  isDeleted?: boolean;
  editedAt?: Date | string | null;
  /** Content removed by retention; only minimal metadata remains. */
  contentExpired?: boolean;
  /** Non-conversational events (reactions, protocol) render as a muted system line. */
  isConversational?: boolean;
  /** Highlighted as AI evidence. */
  highlighted?: boolean;
  /** Attachment / voice note block. */
  children?: React.ReactNode;
  /** Small index label, e.g. "#12", used by evidence links. */
  indexLabel?: string;
  className?: string;
}

const TYPE_ICON: Partial<Record<MessageType, React.ReactNode>> = {
  image: <ImageIcon aria-hidden />,
  video: <Video aria-hidden />,
  document: <FileText aria-hidden />,
  voice: <Mic aria-hidden />,
  audio: <Mic aria-hidden />,
};

/**
 * Read-only transcript bubble. Inbound (lead) left; outbound (business account) right.
 * Outbound means "sent from the business account", not a verified human author.
 */
export const MessageBubble = React.forwardRef<HTMLDivElement, MessageBubbleProps>(function MessageBubble(
  { id, direction, sentAt, timeZone, type = 'text', body, isDeleted, editedAt, contentExpired, isConversational = true, highlighted, children, indexLabel, className },
  ref,
) {
  const outbound = direction === 'outbound';
  const time = formatTime(sentAt, timeZone);
  if (!isConversational) {
    return (
      <div ref={ref} id={id} className={cn('flex justify-center', className)}>
        <p className="rounded-pill bg-surface-subtle px-3 py-1 text-helper text-text-secondary">
          {body ?? MESSAGE_TYPE_LABELS[type]} · <span className="cm-tabular">{time}</span>
        </p>
      </div>
    );
  }
  let content: React.ReactNode;
  if (isDeleted) {
    content = (
      <p className="flex items-center gap-1.5 italic text-text-secondary">
        <Ban aria-hidden className="size-4" /> Mensaje eliminado
      </p>
    );
  } else if (contentExpired) {
    content = (
      <p className="flex items-center gap-1.5 italic text-text-secondary">
        <Hourglass aria-hidden className="size-4" /> Contenido eliminado por la política de retención
      </p>
    );
  } else {
    content = (
      <>
        {type !== 'text' && !children ? (
          <p className="mb-1 flex items-center gap-1.5 text-helper font-medium text-text-secondary [&_svg]:size-4">
            {TYPE_ICON[type] ?? <Paperclip aria-hidden />}
            {MESSAGE_TYPE_LABELS[type]}
          </p>
        ) : null}
        {children}
        {body ? <p className="whitespace-pre-wrap break-words">{body}</p> : null}
      </>
    );
  }
  return (
    <div
      ref={ref}
      id={id}
      tabIndex={highlighted ? -1 : undefined}
      data-direction={direction}
      className={cn('flex scroll-mt-24', outbound ? 'justify-end' : 'justify-start', className)}
    >
      <div
        className={cn(
          'relative max-w-[min(560px,85%)] rounded-[16px] border px-3.5 py-2.5 text-body text-text-primary',
          outbound ? 'rounded-br-[6px] border-brand-primary/15 bg-brand-tint' : 'rounded-bl-[6px] border-border-subtle bg-surface-default',
          highlighted && 'outline-2 outline-offset-2 outline-warning ring-4 ring-warning-bg',
        )}
      >
        <span className="sr-only">{outbound ? 'Cuenta del negocio' : 'Lead'}: </span>
        {content}
        <p className={cn('cm-tabular mt-1 flex items-center justify-end gap-1.5 text-helper text-text-secondary')}>
          {highlighted ? (
            <span className="inline-flex items-center gap-1 font-medium text-warning">
              <AlertTriangle aria-hidden className="size-3.5" /> Evidencia
            </span>
          ) : null}
          {indexLabel ? <span>{indexLabel}</span> : null}
          {editedAt ? (
            <span className="inline-flex items-center gap-0.5">
              <Pencil aria-hidden className="size-3" /> editado
            </span>
          ) : null}
          <time dateTime={new Date(sentAt).toISOString()}>{time}</time>
        </p>
      </div>
    </div>
  );
});

export interface MessageGroupProps {
  direction: Direction;
  /** Sender label, e.g. contact name or "Cuenta · Ventas Norte". */
  senderLabel: string;
  children: React.ReactNode;
  className?: string;
}

export function MessageGroup({ direction, senderLabel, children, className }: MessageGroupProps) {
  return (
    <div role="group" aria-label={senderLabel} className={cn('grid gap-1.5', className)}>
      <p className={cn('px-1 text-helper font-medium text-text-secondary', direction === 'outbound' ? 'text-right' : 'text-left')}>{senderLabel}</p>
      {children}
    </div>
  );
}

export interface TimestampDividerProps {
  label: string;
  /** Emphasised boundary (e.g. new episode). */
  variant?: 'day' | 'episode' | 'gap';
  className?: string;
}

export function TimestampDivider({ label, variant = 'day', className }: TimestampDividerProps) {
  return (
    <div role="separator" aria-label={label} className={cn('my-2 flex items-center gap-3', className)}>
      <span className={cn('h-px flex-1', variant === 'gap' ? 'border-t border-dashed border-warning' : 'bg-border-subtle')} />
      <span
        className={cn(
          'rounded-pill px-3 py-1 text-helper font-medium first-letter:uppercase',
          variant === 'episode' && 'bg-brand-tint text-brand-hover',
          variant === 'day' && 'bg-surface-subtle text-text-secondary',
          variant === 'gap' && 'bg-warning-bg text-warning',
        )}
      >
        {label}
      </span>
      <span className={cn('h-px flex-1', variant === 'gap' ? 'border-t border-dashed border-warning' : 'bg-border-subtle')} />
    </div>
  );
}

export interface TranscriptBlockProps {
  status: TranscriptStatus;
  text?: string | null;
  className?: string;
}

export function TranscriptBlock({ status, text, className }: TranscriptBlockProps) {
  return (
    <div className={cn('rounded-[10px] border border-border-subtle bg-surface-default/70 px-3 py-2', className)}>
      <p className={cn('mb-0.5 text-helper font-medium', status === 'completed' ? 'text-text-secondary' : status === 'failed' || status === 'unavailable' ? 'text-warning' : 'text-text-secondary')}>
        {TRANSCRIPT_STATUS_LABELS[status]}
      </p>
      {status === 'completed' && text ? <p className="whitespace-pre-wrap text-body italic text-text-primary">“{text}”</p> : null}
    </div>
  );
}

export interface VoiceNoteProps {
  durationSeconds?: number | null;
  mediaStatus: MediaStatus;
  /** Authorized same-origin URL (served through the API). Omit when unavailable. */
  audioSrc?: string;
  transcript?: { status: TranscriptStatus; text?: string | null } | null;
  className?: string;
}

/** Voice note: counts as a response event even when not transcribed. */
export function VoiceNote({ durationSeconds, mediaStatus, audioSrc, transcript, className }: VoiceNoteProps) {
  const playable = mediaStatus === 'stored' && audioSrc;
  return (
    <div className={cn('mb-1 grid min-w-[220px] gap-2', className)}>
      <div className="flex items-center gap-2 text-helper font-medium text-text-secondary">
        <Mic aria-hidden className="size-4" />
        Nota de voz <span className="cm-tabular">· {formatDuration(durationSeconds ?? null)}</span>
      </div>
      {playable ? (
        <audio controls preload="none" src={audioSrc} className="h-9 w-full">
          Tu navegador no puede reproducir audio.
        </audio>
      ) : (
        <p className="text-helper text-text-secondary">
          {mediaStatus === 'stored'
            ? 'Audio guardado (reproducción no disponible en esta vista)'
            : mediaStatus === 'pending'
            ? 'Descargando audio…'
            : mediaStatus === 'too_large'
              ? 'Audio demasiado grande para procesar'
              : mediaStatus === 'skipped'
                ? 'Audio no descargado (no requerido)'
                : 'Audio no disponible'}
        </p>
      )}
      {transcript ? <TranscriptBlock status={transcript.status} text={transcript.text} /> : null}
    </div>
  );
}
